import {useCallback, useEffect, useState} from "react";
import useEmblaCarousel from "embla-carousel-react";
import {ArrowUpRight, Github} from "lucide-react";
import Reveal from "./Reveal";
import PhoneMockup from "./PhoneMockup";

const Android = ({className = ""}) => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        aria-hidden="true"
    >
        <path d="m8 7-2-3M16 7l2-3"/>
        <path d="M6 10a6 6 0 0 1 12 0v7H6v-7Z"/>
        <path d="M8 17v3M16 17v3M4 11v5M20 11v5"/>
        <path d="M9 10h.01M15 10h.01"/>
    </svg>
);

const hasProjectLink = (link) => typeof link === "string" && link.trim() !== "" && link.trim() !== "#";

// Each screen is either { variant: "<css-screen-key>", label: "..." } for the
// built-in placeholder UI, or { image: "https://.../screenshot.png", label: "..." }
// to drop in a real app screenshot — the phone frame stays the same.
const projects = [
    {
        id: "pupverse",
        name: "Pupverse: Dog Training",
        client: "Hunter Call",
        tagline: "AI-POWERED DOG CARE & TRAINING",
        description:
            "A dog care and training app redesigned for iOS and Android, combining health tracking, guided training, memories, and AI support.",
        contributions: [
            "Redesigned Home, Health, Training, Memories, and Profile with reusable components and consistent empty and populated states.",
            "Built guided command training with checkpoints, progress tracking, and premium lessons.",
            "Developed an AI Trainer with preset prompts, persistent chat history, and free-tier message limits.",
            "Created a photo and video memory timeline with milestones, growth charts, and pet statistics.",
            "Connected health records and in-app reminders to Firebase, including event editing and completion tracking.",
            "Integrated RevenueCat subscriptions, purchase restoration, and premium access for Android and iOS.",
        ],
        tags: ["FLUTTER", "DART", "BLOC", "FIREBASE", "REVENUECAT", "METASDK"],
        screens: [
            {image: "/cases/pupverse/home.png", label: "HOME"},
            {image: "/cases/pupverse/training.png", label: "TRAINING"},
            {image: "/cases/pupverse/memories.png", label: "MEMORIES"},
            {image: "/cases/pupverse/profile.png", label: "PROFILE"},
            {image: "/cases/pupverse/ai_trainer.png", label: "AI TRAINER"},
        ],
        store: "https://apps.apple.com/us/app/puretracker-quit-drinking/id6759677453?l=ru",
        googlePlay: "https://apps.apple.com/nl/app/pupverse-dog-training/id6746752750"
    },
    {
        id: "tracker",
        name: "PureTracker: Quit Drinking",
        client: "Hunter Call",
        tagline: "SOBRIETY TRACKING COMPANION",
        description:
            "A habit-recovery tracking app with onboarding, AI coaching, mood journaling, and an achievement system — built from scratch.",
        contributions: [
            "Designed a 3-screen onboarding flow and a Dashboard tracking sober days, money saved, and health milestones.",
            "Built an AI Coach with preset support prompts and a daily free-conversation limit for freemium users.",
            "Implemented a Journal with mood tracking and AI-generated weekly progress summaries.",
            "Developed an achievement system (3–365 days) with animated badges and social sharing.",
            "Integrated subscriptions via RevenueCat for Android and iOS.",
        ],
        tags: ["FLUTTER", "DART", "BLOC", "FIREBASE", "REVENUECAT", "METASDK"],
        screens: [
            {image: "/cases/tracker/home.png", label: "HOME"},
            {image: "/cases/tracker/journal.png", label: "JOURNAL"},
            {image: "/cases/tracker/motivation.png", label: "MOTIVATION"},
            {image: "/cases/tracker/coach.png", label: "AI COACH"},
        ],
        store: "https://apps.apple.com/us/app/puretracker-quit-drinking/id6759677453?l=ru",
        googlePlay: "#",
    },
    {
        id: "maxidom",
        name: "Maxidom",
        client: "Itema-22",
        tagline: "Home improvement, made simple",
        description:
            "A large-scale e-commerce app for discovering and purchasing everything for home, renovation, and DIY — from product selection to delivery and order tracking.",
        contributions: [
            "Built and launched Order History, reducing order-status support requests by 28% within two months.",
            "Reworked the app-wide navigation architecture and implemented centralized deep-link handling.",
            "Designed a unified analytics module for consistent event processing across multiple analytics platforms.",
            "Delivered core e-commerce features, including online payments and a product reviews and ratings system.",
            "Implemented interactive maps and store availability logic for customer deliveries.",
        ],
        tags: ["FLUTTER", "DART", "BLOC", "FIREBASE", "YANDEX MAPKIT"],
        screens: [
            {image: "/cases/maxidom/home.png", label: "HOME"},
            {image: "/cases/maxidom/product.png", label: "PRODUCT"},
            {image: "/cases/maxidom/delivery.png", label: "DELIVERY"},
        ],
        store: "https://apps.apple.com/us/app/%D0%BC%D0%B0%D0%BA%D1%81%D0%B8%D0%B4%D0%BE%D0%BC-%D0%B4%D0%BB%D1%8F-%D0%B4%D0%BE%D0%BC%D0%B0-%D0%B8-%D1%80%D0%B5%D0%BC%D0%BE%D0%BD%D1%82%D0%B0/id1632396583?l=ru",
        googlePlay: "https://play.google.com/store/apps/details?id=ru.maxidom.ecomm",
    },
    {
        id: "nappy",
        name: "NappyClub",
        client: "Nappy Club",
        tagline: "Everything for confident parenthood",
        description:
            "A maternity companion for pregnancy and a child’s first three years, combining development tracking, planning tools, expert content, and baby shopping in one app.",
        contributions: [
            "Built a custom calendar for tracking pregnancy, child-development milestones, appointments, and everyday tasks.",
            "Developed the in-app store and integrated online payments for a seamless mobile shopping experience.",
            "Implemented user authentication and integrated Mindbox for personalized customer communications.",
            "Instrumented key user journeys with analytics events across Firebase Analytics and AppMetrica.",
        ],
        tags: ["FLUTTER", "DART", "BLOC", "FIREBASE", "MINDBOX"],
        screens: [
            {image: "/cases/nappy/main.png", label: "TODAY"},
            {image: "/cases/nappy/callendar.png", label: "CALENDAR"},
            {image: "/cases/nappy/category.png", label: "SHOP"},
        ],
    },
    {
        id: "chesk",
        name: "CHESK",
        client: "Info-Pro",
        tagline: "Streamlined electricity meter inspections",
        description:
            "A privately distributed enterprise app for utility field teams to plan, carry out, and track inspections of electricity metering points, coordinate daily fieldwork, and monitor completion.",
        contributions: [
            "Stabilized the existing application by resolving UI defects and business-logic issues across core field-service workflows.",
            "Implemented reliable persistence for assigned service locations, including addresses, descriptions, and task configurations.",
            "Built configuration-driven forms with distinct completion records and required fields for each facility and task type.",
            "Implemented photo-based OCR to automatically extract readings and identification data from industrial utility meters.",
            "Built GPS route tracking to record employees’ daily movements and synchronize route data with the backend.",
            "Developed daily work-report generation with on-device persistence and manual submission of completed-task summaries.",
        ],
        tags: ["FLUTTER", "DART", "BLOC", "Yandex MapKit"],
        screens: [
            {image: "/cases/chesk/list.png", imagePosition: "top", label: "TASKS"},
            {image: "/cases/chesk/download_acts.png", label: "ACTS"},
            {image: "/cases/chesk/map.png", label: "MAP"},
        ],
    },
    {
        id: "power",
        name: "Power machines",
        client: "Info-Pro",
        tagline: "EV charging made simple",
        description:
            "A mobile app for EV drivers to choose a charging station, get directions, select a charging type, and start and pay for a session, with real-time updates on charging status and battery percentage.",
        contributions: [
            "Enhanced route planning and navigation to guide users to selected charging stations.",
            "Integrated online payment processing, including saved-card linking and management within the user profile.",
            "Built visual dashboards and reports for charging sessions and charging activity.",
            "Implemented private groups for friends and family, enabling shared location tracking and access to charging data.",
            "Migrated the application from a light interface to a consistent dark theme.",
            "Improved application stability by resolving visual defects and business-logic issues across core user flows.",
        ],
        tags: ["FLUTTER", "DART", "BLOC", "FIREBASE", "MINDBOX"],
        screens: [
            {image: "/cases/machine/charging.png", label: "CHARGING"},
            {image: "/cases/machine/map.png", label: "MAP"},
            {image: "/cases/machine/list.png", label: "LIST"},
            {image: "/cases/machine/stats.png", label: "STATISTICS"},
        ],
    },
];

const ScreenCarousel = ({project}) => {
    const [emblaRef, emblaApi] = useEmblaCarousel({loop: true, align: "center"});
    const [index, setIndex] = useState(0);

    const onSelect = useCallback(() => {
        if (emblaApi) setIndex(emblaApi.selectedScrollSnap());
    }, [emblaApi]);

    useEffect(() => {
        if (!emblaApi) return;
        emblaApi.on("select", onSelect);
        emblaApi.on("reInit", onSelect);
        onSelect();
    }, [emblaApi, onSelect]);

    return (
        <div className="pt-4">
            <div
                ref={emblaRef}
                data-testid={`screen-carousel-${project.id}`}
                className="cursor-grab overflow-hidden active:cursor-grabbing"
            >
                <div className="flex touch-pan-y items-center">
                    {project.screens.map((s, i) => (
                        <div key={s.label} className="min-w-0 flex-[0_0_70%] select-none sm:flex-[0_0_58%]">
                            <div
                                data-testid={`project-screen-${project.id}-${i}`}
                                className={`flex justify-center transition-all duration-500 ease-out ${
                                    i === index ? "scale-100 opacity-100" : "scale-[0.82] opacity-40"
                                }`}
                            >
                                <PhoneMockup
                                    variant={s.variant}
                                    image={s.image}
                                    imagePosition={s.imagePosition}
                                    widthClass="w-52 sm:w-60"
                                    glow={false}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <div className="mt-7 flex items-center justify-center gap-5">
                {project.screens.map((s, i) => (
                    <button
                        key={s.label}
                        data-testid={`screen-tab-${project.id}-${i}`}
                        onClick={() => emblaApi?.scrollTo(i)}
                        className={`border-b pb-1 font-mono text-[9px] tracking-[0.25em] transition-colors duration-300 ${
                            i === index ? "border-accent text-white" : "border-transparent text-zinc-600 hover:text-zinc-400"
                        }`}
                    >
                        {s.label}
                    </button>
                ))}
            </div>
        </div>
    );
};

const ProjectRow = ({project, number, flip}) => (
    <Reveal className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
        <div
            className={`relative overflow-hidden rounded-3xl border border-white/5 bg-surface px-4 pb-8 pt-14 sm:px-8 lg:col-span-6 ${
                flip ? "lg:order-2" : ""
            }`}
            style={{background: "radial-gradient(ellipse 70% 60% at 50% 40%, rgba(59,130,246,0.06), #141417 70%)"}}
        >
      <span className="absolute left-6 top-5 font-mono text-[10px] tracking-[0.3em] text-zinc-600">
        {number} / {project.tagline.toUpperCase()}
      </span>
            <span className="absolute right-6 top-5 font-mono text-[10px] tracking-[0.3em] text-zinc-700">
        {project.screens.length} SCREENS
      </span>
            <ScreenCarousel project={project}/>
        </div>

        <div className={`lg:col-span-6 ${flip ? "lg:order-1 lg:pr-8" : "lg:pl-8"}`}>
            <div className="font-mono text-xs tracking-[0.3em] text-accent">{number}</div>
            <div className="mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <h3 className="font-display text-3xl font-bold tracking-tight text-white md:text-4xl"
                    data-testid={`project-title-${project.id}`}>
                    {project.name}
                </h3>
                <span className="font-display text-2xl font-bold tracking-tight text-zinc-500 md:text-4xl">
          for "{project.client}"
        </span>
            </div>
            <p className="mt-1 font-mono text-[11px] tracking-[0.2em] text-zinc-500">{project.tagline.toUpperCase()}</p>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-zinc-400">{project.description}</p>
            <ul className="mt-5 max-w-lg space-y-2.5">
                {project.contributions.map((c, i) => (
                    <li key={i} className="flex gap-3 text-sm leading-relaxed text-zinc-400">
                        <span className="mt-2 h-px w-4 shrink-0 bg-accent/60"/>
                        {c}
                    </li>
                ))}
            </ul>
            <div className="mt-6 flex flex-wrap gap-2">
                {project.tags.map((t) => (
                    <span
                        key={t}
                        className="rounded-full border border-white/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400 transition-colors duration-300 hover:border-accent/40 hover:text-white"
                    >
            {t}
          </span>
                ))}
            </div>
            {[project.store, project.googlePlay, project.github].some(hasProjectLink) && (
                <div className="mt-8 flex items-center gap-6">
                    {hasProjectLink(project.store) && (
                        <a
                            data-testid={`project-store-${project.id}`}
                            href={project.store}
                            className="group inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] text-zinc-300 transition-colors duration-300 hover:text-accent"
                        >
                            APP STORE
                            <ArrowUpRight
                                className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"/>
                        </a>
                    )}
                    {hasProjectLink(project.googlePlay) && (
                        <a
                            data-testid={`project-google-play-${project.id}`}
                            href={project.googlePlay}
                            className="group inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] text-zinc-300 transition-colors duration-300 hover:text-accent"
                        >
                            <Android className="h-4 w-4"/>
                            GOOGLE PLAY
                            <ArrowUpRight
                                className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"/>
                        </a>
                    )}
                    {hasProjectLink(project.github) && (
                        <a
                            data-testid={`project-github-${project.id}`}
                            href={project.github}
                            className="group inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] text-zinc-300 transition-colors duration-300 hover:text-accent"
                        >
                            <Github className="h-4 w-4"/>
                            GITHUB
                            <ArrowUpRight
                                className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"/>
                        </a>
                    )}
                </div>
            )}
        </div>
    </Reveal>
);

export default function Projects() {
    return (
        <section id="projects" data-testid="projects-section" className="relative py-16 lg:py-24">
            <div className="mx-auto max-w-7xl px-6 lg:px-10">
                <Reveal>
                    <div className="flex items-end justify-between border-b border-white/5 pb-8">
                        <div>
                            <div className="font-mono text-xs tracking-[0.35em] text-accent">[ 01 ]</div>
                            <h2 className="mt-4 font-display text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
                                Last Projects
                            </h2>
                        </div>
                        <p className="hidden max-w-xs text-right text-sm leading-relaxed text-zinc-500 md:block lg:max-w-none lg:whitespace-nowrap">
                            {projects.length} {projects.length === 1 ? "app" : " last apps"}. Built with care to solve real product challenges.
                        </p>
                    </div>
                </Reveal>
                <div className="mt-16 space-y-20 lg:space-y-24">
                    {projects.map((p, i) => (
                        <ProjectRow key={p.id} project={p} number={String(i + 1).padStart(3, "0")} flip={i % 2 === 1}/>
                    ))}
                </div>
            </div>
        </section>
    );
}
